/**
 * Root App: router + auth bootstrap. On mount, re-derive the session from the
 * httpOnly refresh cookie via /api/auth/me (nothing auth-related is persisted
 * client-side). Unauthenticated users are redirected to /login.
 */

import { useEffect } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { RequireAuth, RequireSuperAdmin } from "@/components/auth/RequireAuth";
import { AppLayout } from "@/components/layout/AppLayout";
import { LoginPage } from "@/pages/LoginPage";
import { TenantsPage } from "@/pages/TenantsPage";
import { TenantWorkspacePage } from "@/pages/TenantWorkspacePage";
import { ApiKeysPage } from "@/pages/ApiKeysPage";
import { CollectionsPage } from "@/pages/CollectionsPage";
import { DocumentsPage } from "@/pages/DocumentsPage";
import { KnowledgeBasesPage } from "@/pages/KnowledgeBasesPage";
import { PlaygroundSearchPage } from "@/pages/PlaygroundSearchPage";
import { PlaygroundChatPage } from "@/pages/PlaygroundChatPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { AnalyticsPage } from "@/pages/AnalyticsPage";
import { AuditPage } from "@/pages/AuditPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { InstancesPage } from "@/pages/InstancesPage";
import { UsersPage } from "@/pages/UsersPage";

function Protected({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppLayout>{children}</AppLayout>
    </RequireAuth>
  );
}

function RedirectWriterToDocuments() {
  const { tenantId } = useParams();
  return <Navigate to={tenantId ? `/tenants/${encodeURIComponent(tenantId)}/documents` : "/documents"} replace />;
}

export function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  const status = useAuth((s) => s.status);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Block rendering until the bootstrap /me resolves, so we don't flash /login.
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route path="/" element={<Protected><DashboardPage /></Protected>} />

      {/* SuperAdmin-only management */}
      <Route path="/instances" element={
        <RequireAuth><RequireSuperAdmin><AppLayout><InstancesPage /></AppLayout></RequireSuperAdmin></RequireAuth>
      } />
      <Route path="/users" element={
        <RequireAuth><RequireSuperAdmin><AppLayout><UsersPage /></AppLayout></RequireSuperAdmin></RequireAuth>
      } />

      {/* Phase 2 CRUDL screens */}
      <Route path="/tenants" element={<Protected><TenantsPage /></Protected>} />
      <Route path="/tenants/:tenantId" element={<Protected><TenantWorkspacePage /></Protected>} />
      <Route path="/tenants/:tenantId/collections" element={<Protected><CollectionsPage /></Protected>} />
      <Route path="/tenants/:tenantId/documents" element={<Protected><DocumentsPage /></Protected>} />
      <Route path="/tenants/:tenantId/knowledge-base" element={<Protected><KnowledgeBasesPage /></Protected>} />
      <Route path="/tenants/:tenantId/api-keys" element={<Protected><ApiKeysPage /></Protected>} />
      <Route path="/tenants/:tenantId/playground/search" element={<Protected><PlaygroundSearchPage /></Protected>} />
      <Route path="/tenants/:tenantId/playground/chat" element={<Protected><PlaygroundChatPage /></Protected>} />
      <Route path="/tenants/:tenantId/playground/writer" element={<Protected><RedirectWriterToDocuments /></Protected>} />
      <Route path="/tenants/:tenantId/analytics" element={<Protected><AnalyticsPage /></Protected>} />
      <Route path="/collections" element={<Protected><CollectionsPage /></Protected>} />
      <Route path="/documents" element={<Protected><DocumentsPage /></Protected>} />
      <Route path="/knowledge-bases" element={<Protected><KnowledgeBasesPage /></Protected>} />
      <Route path="/api-keys" element={<Protected><ApiKeysPage /></Protected>} />
      <Route path="/audit" element={<Protected><AuditPage /></Protected>} />

      {/* Phase 3 interaction */}
      <Route path="/playground/search" element={<Protected><PlaygroundSearchPage /></Protected>} />
      <Route path="/playground/chat" element={<Protected><PlaygroundChatPage /></Protected>} />
      <Route path="/playground/writer" element={<Protected><RedirectWriterToDocuments /></Protected>} />
      <Route path="/analytics" element={<Protected><AnalyticsPage /></Protected>} />

      <Route path="/change-password" element={<Protected><ChangePasswordPage /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
